/**
 * useQBridgeStatus Hook
 * Polls QBridge local server to check if it's running
 *
 * v1.9.0+ Features:
 * - Supports new /install-info endpoint for comprehensive status
 * - Returns MT5, auth, and chartBridge status in addition to QBridge status
 * - Backward compatible with legacy endpoint
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
    checkQBridgeStatus,
    getInstallInfo,
    QBRIDGE_STATUS,
    POLLING_INTERVALS
} from '../services/qbridge.service';

/**
 * Hook for monitoring QBridge desktop app status
 * @param {boolean} enabled - Whether polling is active
 * @param {number} interval - Polling interval in ms (default 2000)
 * @param {boolean} useInstallInfo - Use new /install-info endpoint (default true)
 * @returns {{
 *   status: string,
 *   version: string | null,
 *   isRunning: boolean,
 *   isChecking: boolean,
 *   error: string | null,
 *   installInfo: object | null,
 *   mt5Connected: boolean,
 *   mt5Account: string | null,
 *   mt5Broker: string | null,
 *   authorized: boolean,
 *   ready: boolean,
 *   refresh: () => Promise<void>
 * }}
 */
const useQBridgeStatus = (
    enabled = true,
    interval = POLLING_INTERVALS.QBRIDGE_STATUS,
    useInstallInfo = true
) => {
    const [status, setStatus] = useState(QBRIDGE_STATUS.UNKNOWN);
    const [version, setVersion] = useState(null);
    const [isChecking, setIsChecking] = useState(false);
    const [error, setError] = useState(null);
    const [installInfo, setInstallInfo] = useState(null);
    const [mt5Connected, setMt5Connected] = useState(false);
    const [mt5Account, setMt5Account] = useState(null);
    const [mt5Broker, setMt5Broker] = useState(null);
    const [authorized, setAuthorized] = useState(false);
    const [ready, setReady] = useState(false);
    const intervalRef = useRef(null);
    const mountedRef = useRef(true);

    const checkStatus = useCallback(async () => {
        if (!mountedRef.current) return;

        setIsChecking(true);

        if (useInstallInfo) {
            // Use new comprehensive endpoint
            const result = await getInstallInfo();

            if (!mountedRef.current) return;

            if (result.success && result.qbridge?.running) {
                setStatus(QBRIDGE_STATUS.READY);
                setVersion(result.qbridge.version);
                setInstallInfo(result);
                setMt5Connected(result.mt5?.connected || false);
                setMt5Account(result.mt5?.account || null);
                setMt5Broker(result.mt5?.broker || null);
                setAuthorized(result.auth?.authorized || false);
                setReady(result.ready || false);
                setError(null);
            } else {
                setStatus(QBRIDGE_STATUS.NOT_RUNNING);
                setVersion(null);
                setInstallInfo(null);
                setMt5Connected(false);
                setMt5Account(null);
                setMt5Broker(null);
                setAuthorized(false);
                setReady(false);
                setError(result.error);
            }
        } else {
            // Use legacy endpoint
            const result = await checkQBridgeStatus();

            if (!mountedRef.current) return;

            setStatus(result.status);
            setVersion(result.version);
            setError(result.error);
        }

        setIsChecking(false);
    }, [useInstallInfo]);

    // Initial check and polling setup
    useEffect(() => {
        mountedRef.current = true;

        if (enabled) {
            // Initial check immediately
            checkStatus();

            // Set up polling
            intervalRef.current = setInterval(checkStatus, interval);
        }

        return () => {
            mountedRef.current = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [enabled, interval, checkStatus]);

    const refresh = useCallback(async () => {
        return checkStatus();
    }, [checkStatus]);

    return {
        status,
        version,
        isRunning: status === QBRIDGE_STATUS.READY,
        isChecking,
        error,
        installInfo,
        mt5Connected,
        mt5Account,
        mt5Broker,
        authorized,
        ready,
        refresh
    };
};

export default useQBridgeStatus;
